import { Trip } from '../../services/trips/api/tripsApi';
import { formatDestinations } from '../../services/trips/utils/tripUtils';
import { formatTripDates } from '../../services/trips/utils/dateUtils';
import { extractTripUuid } from '../../services/trips/utils/idUtils';

export interface ProfileTripPreview {
  id: string;
  title: string;
  meta: string;
  tags: string[];
  description: string;
  imageUrl: string;
  sourceTrip: Trip;
}

export interface ProfileTripTagPreview {
  visible: string[];
  hasMore: boolean;
}

const fallbackTripImages = [
  'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1513735492246-483525079686?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?auto=format&fit=crop&w=900&q=80',
];

export const mapTripToProfilePreview = (trip: Trip, index = 0): ProfileTripPreview => {
  const destinations = trip.destinations?.length ? trip.destinations : [];
  const duration = getTripDuration(trip);
  const dateRange = formatTripDates(trip.dates);
  const imageUrl = trip.images?.find((image) => /^https?:\/\//.test(image));
  const tripMonthYear = formatTripMonthYear(trip);

  return {
    id: extractTripUuid(trip.id),
    title: trip.title,
    meta: `${tripMonthYear} · ${duration}`,
    tags: destinations.map((destination) => destination.trim()).filter(Boolean),
    description: trip.description || `${dateRange} across ${formatDestinations(destinations)}.`,
    imageUrl: imageUrl || fallbackTripImages[index % fallbackTripImages.length],
    sourceTrip: trip,
  };
};

export const getVisibleTripTags = (tags: string[]): ProfileTripTagPreview => {
  const cleaned = [...new Set(tags.map((tag) => tag.trim()).filter(Boolean))];
  if (!cleaned.length) {
    return { visible: [], hasMore: false };
  }

  const hasLongTag = cleaned.some((tag) => tag.length > 10);
  const maxTags = hasLongTag || cleaned.join('').length > 26 ? 3 : 4;
  const visible = cleaned.slice(0, maxTags);

  return {
    visible,
    hasMore: cleaned.length > visible.length,
  };
};

const formatTripMonthYear = (trip: Trip) => {
  if (!trip.dates?.startDate) {
    return destinationFallback(trip);
  }

  const start = new Date(trip.dates.startDate);
  if (Number.isNaN(start.getTime())) {
    return destinationFallback(trip);
  }

  return start.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
};

const destinationFallback = (trip: Trip) => {
  const destinations = trip.destinations?.length ? trip.destinations : [];
  return destinations[0] || formatDestinations(destinations) || 'Dates TBD';
};

const getTripDuration = (trip: Trip) => {
  if (!trip.dates?.startDate || !trip.dates?.endDate) return 'Dates TBD';

  const start = new Date(trip.dates.startDate);
  const end = new Date(trip.dates.endDate);
  const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  if (!Number.isFinite(diff) || diff <= 0) {
    return 'Dates TBD';
  }

  return `${diff} ${diff === 1 ? 'day' : 'days'}`;
};
